import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { toast } from "../../../../utils/toast";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { connectParis } from "../../../../api/integrations";
import { iconList } from "../../../../utils/iconList";
import "../ModernIntegrations.scss";

// Paris se conecta con la API key del Seller Center (Mi Cuenta → Integraciones).
// El backend la valida contra Paris antes de guardarla (cifrada), así que un
// error de key se muestra en el campo y no como un fallo genérico.
export default function AddParisModal({ open, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef(null);

  // El foco inicial del Modal cae en el botón de cerrar; lo movemos al campo.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [open]);

  const trimmed = apiKey.trim();

  const handleSubmit = async () => {
    if (saving) return;
    if (!trimmed) {
      setFieldError(t("Ingresa la API key para continuar."));
      inputRef.current?.focus();
      return;
    }
    setSaving(true);
    setFieldError("");
    try {
      const res = await connectParis({ apiKey: trimmed });
      if (res?.ok) {
        toast.success(t("Cuenta de Paris conectada. Tus pedidos listos para despacho empezarán a llegar en unos minutos."));
        onClose();
        if (typeof onSuccess === "function") onSuccess();
        return;
      }
      if (res?.status === 422) {
        setFieldError(t("Paris rechazó la API key. Cópiala de nuevo desde tu Seller Center."));
      } else if (res?.status === 409) {
        setFieldError(t("Esta cuenta de Paris ya está conectada a otra empresa en Beartrack."));
      } else if (res?.status === 429) {
        setFieldError(t("Demasiados intentos. Espera un minuto y vuelve a probar."));
      } else {
        toast.error(t("No se pudo conectar con Paris. Intenta de nuevo en unos minutos."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Conectar Paris")}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={saving || !trimmed}
          >
            {saving ? t("Conectando...") : t("Conectar")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="mig-logo" aria-hidden="true">
            <img src={iconList.paris} alt="" className="mig-logo__img dark-safe-img" />
          </span>
          <p className="text-sm text-fg-muted">
            {t(
              "Pega la API key de tu Seller Center de Paris (Mi Cuenta → Integraciones). Traeremos tus pedidos listos para despacho y podrás ver sus etiquetas.",
            )}
          </p>
        </div>

        <div className="space-y-1.5">
          <FieldLabel htmlFor={inputId}>{t("API key de Paris")}</FieldLabel>
          <Input
            id={inputId}
            ref={inputRef}
            type="password"
            value={apiKey}
            placeholder={t("Pega aquí tu API key")}
            autoComplete="off"
            spellCheck={false}
            invalid={!!fieldError}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? errorId : undefined}
            onChange={(e) => {
              setApiKey(e.target.value);
              if (fieldError) setFieldError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !saving) handleSubmit();
            }}
          />
          <p
            id={errorId}
            aria-live="polite"
            className="flex min-h-[1rem] items-center gap-1 text-xs text-status-error"
          >
            {fieldError && (
              <>
                <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {fieldError}
              </>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}
